/**
 * Serviço para gerenciar provedores de IA
 */

import { getToken } from "./tokenService";
import type {
  AIProvider,
  CreateAIProviderRequest,
  UpdateAIProviderRequest,
  AIProvidersListResponse,
  AIProvidersStats,
} from "@/types/aiProviders";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista provedores de IA com filtros e paginação
 */
export async function listAIProviders(options?: {
  limit?: number;
  offset?: number;
  is_active?: boolean;
  search?: string;
}): Promise<ApiResponse<AIProvidersListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.is_active !== undefined)
    params.append("is_active", options.is_active.toString());
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  const endpoint = `/ai-providers${queryString ? `?${queryString}` : ""}`;

  return apiRequest<AIProvidersListResponse>(endpoint);
}

/**
 * Obtém um provedor de IA por ID
 * SEGURANÇA: Retorna auth_config CRIPTOGRAFADO (não expõe dados sensíveis)
 */
export async function getAIProviderById(
  id: number
): Promise<ApiResponse<AIProvider>> {
  return apiRequest<AIProvider>(`/ai-providers/${id}`);
}

/**
 * Obtém as credenciais descriptografadas de um provedor
 * IMPORTANTE: Use apenas quando necessário (edição)
 */
export async function getAIProviderCredentials(
  id: number
): Promise<ApiResponse<AIProvider>> {
  return apiRequest<AIProvider>(`/ai-providers/${id}/credentials`);
}

/**
 * Cria um novo provedor de IA
 */
export async function createAIProvider(
  providerData: CreateAIProviderRequest
): Promise<ApiResponse<AIProvider>> {
  return apiRequest<AIProvider>("/ai-providers", "POST", providerData);
}

/**
 * Atualiza um provedor de IA existente
 */
export async function updateAIProvider(
  id: number,
  providerData: UpdateAIProviderRequest
): Promise<ApiResponse<AIProvider>> {
  return apiRequest<AIProvider>(`/ai-providers/${id}`, "PUT", providerData);
}

/**
 * Remove um provedor de IA
 */
export async function deleteAIProvider(id: number): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/ai-providers/${id}`, "DELETE");
}

/**
 * Alterna o status de um provedor (ativo/inativo)
 */
export async function toggleAIProviderStatus(
  id: number,
  isActive: boolean
): Promise<ApiResponse<AIProvider>> {
  return apiRequest<AIProvider>(`/ai-providers/${id}/toggle-status`, "PATCH", {
    is_active: isActive,
  });
}

/**
 * Obtém estatísticas dos provedores de IA
 */
export async function getAIProvidersStats(): Promise<
  ApiResponse<AIProvidersStats>
> {
  return apiRequest<AIProvidersStats>("/ai-providers/stats");
}

/**
 * Busca modelos disponíveis da API do Google Gemini
 */
export async function fetchGoogleModels(
  providerId: number
): Promise<ApiResponse<{ models: any[]; nextPageToken?: string }>> {
  return apiRequest<{ models: any[]; nextPageToken?: string }>(
    `/ai-providers/${providerId}/fetch-models`
  );
}
